/**
 * 多画布库（P1 C-3c）：把「一张活动画布」升级成「一个可列出的画布集合」。
 *
 * 分层纪律（与导演台 projectIO 同一套路）：这里全是纯逻辑，storage 注入、
 * 可离线测；window 只在缺省解析时触达，并用 typeof window 守卫，测试环境不碰。
 *
 * 存储布局（各键独立，结构大改时换后缀自然作废而非污染）：
 *  · index  —— CanvasMeta[] 轻量索引，供首页列出（不含节点正文，读列表不必解析全文）；
 *  · item.<id> —— 每张画布的完整 CanvasDoc；
 *  · active —— 上次打开的画布 id，刷新后回到那张；
 *  · 早期单画布快照 doc.v1 —— 首次访问时一次性迁移为库中的一张，随后删除旧键。
 *
 * 一切从 item 键读回的文档都必须过 loadCanvasDocument（迁移→校验），绝不裸 JSON 断言。
 */
import { createEmptyCanvas, type CanvasDoc, type CanvasEdge, type CanvasNode } from '../../model/canvas';
import { isSubjectPayload } from '../../model/subject';
import { loadCanvasDocument } from '../../model/persist';

/** 只用到的存储面，便于测试注入假实现 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** 画布轻量元信息：首页列表用它，点开某张才去 loadCanvas 取全文 */
export interface CanvasMeta {
  id: string;
  title: string;
  updatedAt: string;
  nodeCount: number;
  /** 封面缩略：取首个带媒体节点的缩略图（首页卡用，D-51）；无媒体则 undefined */
  cover?: string | undefined;
}

const INDEX_KEY = 'videoai-pro.canvas.index.v1';
const ACTIVE_KEY = 'videoai-pro.canvas.active.v1';
const DOC_PREFIX = 'videoai-pro.canvas.item.';
/** 早期（C-2b~C-3b）只存一张活动画布时用的键，迁移后废弃 */
const LEGACY_DOC_KEY = 'videoai-pro.canvas.doc.v1';

function docKey(id: string): string {
  return DOC_PREFIX + id;
}

function resolve(storage: StorageLike | undefined): StorageLike | null {
  if (storage !== undefined) {
    return storage;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null; // 隐私模式等禁用 localStorage：静默降级为不持久化
  }
}

function readIndex(s: StorageLike): CanvasMeta[] {
  const raw = s.getItem(INDEX_KEY);
  if (raw === null) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CanvasMeta[]) : [];
  } catch {
    return [];
  }
}

function writeIndex(s: StorageLike, list: CanvasMeta[]): void {
  try {
    s.setItem(INDEX_KEY, JSON.stringify(list));
  } catch {
    /* 配额异常：索引写失败不阻断，内存态仍生效 */
  }
}

function upsertIndex(s: StorageLike, meta: CanvasMeta): void {
  const rest = readIndex(s).filter((m) => m.id !== meta.id);
  rest.push(meta);
  writeIndex(s, rest);
}

function removeFromIndex(s: StorageLike, id: string): void {
  writeIndex(
    s,
    readIndex(s).filter((m) => m.id !== id),
  );
}

/** 从完整文档抽出元信息（纯函数，可测）；顺带取首个带媒体节点作封面 */
export function metaOf(doc: CanvasDoc): CanvasMeta {
  const coverNode = doc.nodes.find((n) => n.media !== undefined);
  const cover = coverNode?.media?.thumbnail ?? coverNode?.media?.url;
  return { id: doc.id, title: doc.title, updatedAt: doc.updatedAt, nodeCount: doc.nodes.length, cover };
}

/** 保存画布：写全文 + upsert 索引。返回元信息；无存储或写失败返回 null */
export function saveCanvas(doc: CanvasDoc, storage?: StorageLike): CanvasMeta | null {
  const s = resolve(storage);
  if (s === null) {
    return null;
  }
  try {
    s.setItem(docKey(doc.id), JSON.stringify(doc));
  } catch {
    return null;
  }
  const meta = metaOf(doc);
  upsertIndex(s, meta);
  return meta;
}

/** 载入指定画布；不存在或结构损坏返回 null（损坏绝不半读） */
export function loadCanvas(id: string, storage?: StorageLike): CanvasDoc | null {
  const s = resolve(storage);
  if (s === null) {
    return null;
  }
  const raw = s.getItem(docKey(id));
  if (raw === null) {
    return null;
  }
  try {
    const res = loadCanvasDocument(JSON.parse(raw) as unknown);
    return res.ok ? res.doc : null;
  } catch {
    return null;
  }
}

/** 删除画布：移除全文 + 索引项；若删的是活动画布则清活动指针。返回是否确有此项 */
export function deleteCanvas(id: string, storage?: StorageLike): boolean {
  const s = resolve(storage);
  if (s === null) {
    return false;
  }
  const existed = s.getItem(docKey(id)) !== null;
  s.removeItem(docKey(id));
  removeFromIndex(s, id);
  if (s.getItem(ACTIVE_KEY) === id) {
    s.removeItem(ACTIVE_KEY);
  }
  return existed;
}

export function setActiveId(id: string, storage?: StorageLike): void {
  const s = resolve(storage);
  if (s === null) {
    return;
  }
  try {
    s.setItem(ACTIVE_KEY, id);
  } catch {
    /* 忽略 */
  }
}

export function getActiveId(storage?: StorageLike): string | null {
  const s = resolve(storage);
  if (s === null) {
    return null;
  }
  return s.getItem(ACTIVE_KEY);
}

/** 列出全部画布，按更新时间倒序（最近编辑在前）。顺带触发一次性迁移 */
export function listCanvases(storage?: StorageLike): CanvasMeta[] {
  const s = resolve(storage);
  if (s === null) {
    return [];
  }
  migrateLegacy(s);
  return readIndex(s)
    .slice()
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)); // ISO 串字典序即时间序，倒序=最近在前
}

/** 首页数据概览（真实统计，非假数据）：从库中聚合。 */
export interface HomeStats {
  projects: number;
  nodes: number;
  /** 主体资产卡数（角色/场景/道具） */
  assets: number;
  /** 已生成媒体节点数（图/视频/音频） */
  generations: number;
  /** 分镜节点数 */
  shots: number;
}

/** 遍历索引载入每张画布，聚合首页概览数字（storage 注入可测）。 */
export function getHomeStats(storage?: StorageLike): HomeStats {
  const metas = listCanvases(storage);
  let nodes = 0;
  let assets = 0;
  let generations = 0;
  let shots = 0;
  for (const m of metas) {
    nodes += m.nodeCount;
    const doc = loadCanvas(m.id, storage);
    if (doc === null) {
      continue;
    }
    for (const n of doc.nodes) {
      if (n.kind === 'subject') {
        assets += 1;
      }
      if (n.media !== undefined) {
        generations += 1;
      }
      if (n.kind === 'shot') {
        shots += 1;
      }
    }
  }
  return { projects: metas.length, nodes, assets, generations, shots };
}

/** 生成一个低冲突的画布 id（时间戳 + 随机尾，纯逻辑可测其前缀） */
export function newCanvasId(now: number = Date.now()): string {
  const rand = Math.random().toString(36).slice(2, 7);
  return `canvas-${now.toString(36)}-${rand}`;
}

/** 新建一张空画布（不落盘，交给 store 的持久化订阅统一写） */
export function createBlankCanvas(nowIso: string, id: string = newCanvasId()): CanvasDoc {
  return createEmptyCanvas(id, nowIso);
}

/**
 * 一次性迁移：把早期单画布快照导入为库中的一张。
 * 无论成功失败都删旧键，避免每次启动重复迁移；已有索引则只删旧键不导入（防覆盖新数据）。
 */
export function migrateLegacy(storage?: StorageLike): void {
  const s = resolve(storage);
  if (s === null) {
    return;
  }
  const raw = s.getItem(LEGACY_DOC_KEY);
  if (raw === null) {
    return;
  }
  try {
    if (readIndex(s).length === 0) {
      const res = loadCanvasDocument(JSON.parse(raw) as unknown);
      if (res.ok) {
        saveCanvas(res.doc, s);
        setActiveId(res.doc.id, s);
      }
    }
  } catch {
    /* 坏数据直接丢弃 */
  }
  s.removeItem(LEGACY_DOC_KEY);
}

/* ---------- 画布模板（P-A3：选中子图存为模板、导入复用） ---------- */

const TPL_INDEX_KEY = 'videoai-pro.canvas.templates.index.v1';
const TPL_ITEM_PREFIX = 'videoai-pro.canvas.tpl.';

export interface CanvasTemplate {
  id: string;
  name: string;
  updatedAt: string;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
}

export interface TemplateMeta {
  id: string;
  name: string;
  updatedAt: string;
  nodeCount: number;
  cover?: string | undefined;
}

function tplItemKey(id: string): string {
  return TPL_ITEM_PREFIX + id;
}

function readTplIndex(s: StorageLike): TemplateMeta[] {
  const raw = s.getItem(TPL_INDEX_KEY);
  if (raw === null) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TemplateMeta[]) : [];
  } catch {
    return [];
  }
}

function writeTplIndex(s: StorageLike, list: TemplateMeta[]): void {
  try {
    s.setItem(TPL_INDEX_KEY, JSON.stringify(list));
  } catch {
    /* 配额异常不阻断 */
  }
}

/** 模板 id（时间戳+随机尾）。 */
export function newTemplateId(now: number = Date.now()): string {
  const rand = Math.random().toString(36).slice(2, 7);
  return `tpl-${now.toString(36)}-${rand}`;
}

/** 保存一份模板（节点/边为选中子图快照）。 */
export function saveTemplate(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  name: string,
  storage?: StorageLike,
  id: string = newTemplateId(),
): TemplateMeta | null {
  const s = resolve(storage);
  if (s === null) {
    return null;
  }
  const nowIso = new Date().toISOString();
  const tpl: CanvasTemplate = { id, name, updatedAt: nowIso, nodes, edges };
  try {
    s.setItem(tplItemKey(id), JSON.stringify(tpl));
  } catch {
    return null;
  }
  const coverNode = nodes.find((n) => n.media !== undefined);
  const meta: TemplateMeta = {
    id,
    name,
    updatedAt: nowIso,
    nodeCount: nodes.length,
    cover: coverNode?.media?.thumbnail ?? coverNode?.media?.url,
  };
  writeTplIndex(s, [...readTplIndex(s).filter((m) => m.id !== id), meta]);
  return meta;
}

/** 列出模板（最近在前）。 */
export function listTemplates(storage?: StorageLike): TemplateMeta[] {
  const s = resolve(storage);
  if (s === null) {
    return [];
  }
  return readTplIndex(s)
    .slice()
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/** 载入一份模板（无则 null）。 */
export function loadTemplate(id: string, storage?: StorageLike): CanvasTemplate | null {
  const s = resolve(storage);
  if (s === null) {
    return null;
  }
  const raw = s.getItem(tplItemKey(id));
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as CanvasTemplate;
    return Array.isArray(parsed.nodes) && Array.isArray(parsed.edges) ? parsed : null;
  } catch {
    return null;
  }
}

/** 删除模板。 */
export function deleteTemplate(id: string, storage?: StorageLike): boolean {
  const s = resolve(storage);
  if (s === null) {
    return false;
  }
  const existed = s.getItem(tplItemKey(id)) !== null;
  s.removeItem(tplItemKey(id));
  writeTplIndex(s, readTplIndex(s).filter((m) => m.id !== id));
  return existed;
}

/**
 * 重映射模板为当前画布可注入的节点/边：nextId() 供全新 id（内部 memoize 保证同一旧 id
 * 始终得到同一新 id，边才能对上节点）、整体偏移落点。纯函数可测。
 */
export function remapTemplate(
  tpl: CanvasTemplate,
  nextId: () => string,
  offset: { x: number; y: number },
): { nodes: CanvasNode[]; edges: CanvasEdge[] } {
  const map = new Map<string, string>();
  const idFor = (old: string): string => {
    let v = map.get(old);
    if (v === undefined) {
      v = nextId();
      map.set(old, v);
    }
    return v;
  };
  const nodes = tpl.nodes.map((n) => ({
    ...n,
    id: idFor(n.id),
    position: { x: n.position.x + offset.x, y: n.position.y + offset.y },
  }));
  const edges = tpl.edges.map((e) => {
    const from = idFor(e.from);
    const to = idFor(e.to);
    return { id: `e-${from}->${to}`, from, to };
  });
  return { nodes, edges };
}

/** 多宫格布局：接近正方形的列/行数（count≥1）。纯函数可测。 */
export function gridLayout(count: number): { cols: number; rows: number } {
  const n = Math.max(1, Math.floor(count));
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  return { cols, rows };
}

/* ---------- 资产库聚合（P-D②）：跨画布采集已生成媒体 + 主体资产 ---------- */

export interface MediaAsset {
  canvasId: string;
  canvasTitle: string;
  nodeId: string;
  kind: 'image' | 'video' | 'audio';
  title: string;
  url: string;
  thumbnail?: string | undefined;
}

export interface SubjectAsset {
  canvasId: string;
  canvasTitle: string;
  nodeId: string;
  category: 'character' | 'scene' | 'prop';
  name: string;
  description: string;
  thumb?: string | undefined;
}

export interface ShotAsset {
  canvasId: string;
  canvasTitle: string;
  nodeId: string;
  title: string;
}

export interface AssetBundle {
  media: MediaAsset[];
  subjects: SubjectAsset[];
  shots: ShotAsset[];
}

/** 遍历库中每张画布，聚合媒体资产与主体资产（storage 注入可测）。 */
export function collectAssets(storage?: StorageLike): AssetBundle {
  const media: MediaAsset[] = [];
  const subjects: SubjectAsset[] = [];
  const shots: ShotAsset[] = [];
  for (const m of listCanvases(storage)) {
    const doc = loadCanvas(m.id, storage);
    if (doc === null) {
      continue;
    }
    for (const n of doc.nodes) {
      if (isSubjectPayload(n.payload)) {
        subjects.push({
          canvasId: doc.id,
          canvasTitle: doc.title,
          nodeId: n.id,
          category: n.payload.category,
          name: n.payload.name,
          description: n.payload.description,
          thumb: n.media?.thumbnail ?? n.media?.url,
        });
      } else if (n.media !== undefined && (n.kind === 'image' || n.kind === 'video' || n.kind === 'audio')) {
        media.push({
          canvasId: doc.id,
          canvasTitle: doc.title,
          nodeId: n.id,
          kind: n.kind,
          title: n.title,
          url: n.media.url,
          thumbnail: n.media.thumbnail,
        });
      } else if (n.kind === 'shot') {
        shots.push({ canvasId: doc.id, canvasTitle: doc.title, nodeId: n.id, title: n.title });
      }
    }
  }
  return { media, subjects, shots };
}

/** 从指定画布永久移除一个节点（及其相连边）并落盘。用于回收站“彻底删除”。 */
export function removeNodeFromCanvas(canvasId: string, nodeId: string, storage?: StorageLike): void {
  const doc = loadCanvas(canvasId, storage);
  if (doc === null) {
    return;
  }
  const nodes = doc.nodes.filter((n) => n.id !== nodeId);
  const edges = doc.edges.filter((e) => e.from !== nodeId && e.to !== nodeId);
  saveCanvas({ ...doc, nodes, edges }, storage);
}
